import { Plus, Stethoscope } from "lucide-react";

import type { Patient } from "@elation/api-client/src/stitch-web/models";

import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Avatar, AvatarFallback } from "@src/shared/components/ui/avatar";
import { Button } from "@src/shared/components/ui/button";

import { PatientEntity } from "../../../../domain";

export interface PatientDetailHeaderV2Props {
  patient?: Patient | null;
  isStartingVisit?: boolean;
  onStartVisit: () => void;
}

export function PatientDetailHeaderV2({
  patient,
  isStartingVisit = false,
  onStartVisit,
}: PatientDetailHeaderV2Props) {
  const patientName = PatientEntity.displayName(patient) ?? "Unknown patient";
  const patientMetadata = PatientEntity.listMetadata(patient);

  return (
    <HorizontalFlexbox
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      className="gap-4 rounded-card border border-border bg-surface p-card shadow-soft"
    >
      <HorizontalFlexbox alignItems="center" className="min-w-0 gap-3">
        <Avatar className="size-12 border border-border bg-background">
          <AvatarFallback className="bg-primary/10 font-mono text-[12px] font-semibold text-primary">
            {PatientEntity.initials(patient) ?? "PT"}
          </AvatarFallback>
        </Avatar>
        <VerticalFlexbox className="min-w-0 gap-1">
          <h1 className="truncate text-[22px] font-semibold tracking-[-0.025em] text-surface-foreground">
            {patientName}
          </h1>
          <p className="font-mono text-[11px] text-muted-foreground">
            {patientMetadata ?? "Patient details"}
          </p>
        </VerticalFlexbox>
      </HorizontalFlexbox>

      <Button
        type="button"
        className="h-9 rounded-[9px] px-4 text-[13px] font-semibold"
        disabled={isStartingVisit}
        onClick={onStartVisit}
      >
        {isStartingVisit ? (
          <Stethoscope className="size-4 animate-pulse" aria-hidden="true" />
        ) : (
          <Plus className="size-4" aria-hidden="true" />
        )}
        Start a visit
      </Button>
    </HorizontalFlexbox>
  );
}
